/** /ai-changes/:id — one change set (backend metaplan A3c, AIE §10).
 *
 * Read top to bottom: who made it and how (actor, credential, MCP client and grant, console
 * token, request and login IPs), when, why, what it cost; the proposal it applied; its revert
 * links; the records it changed, each with its before → after; and the change-history rows it
 * wrote. "Revert change set…" opens the revert plan (`RevertChangeSetDialog`) — shown only to an
 * operator who may revert and only while the change set can be reverted. A content or vocabulary
 * change set also needs the console permission of each kind it changes (client MCP WPS P3-16):
 * an operator who may revert, but not those kinds, sees Revert disabled with why.
 *
 * A content apply records one change set per record, all in one batch (client MCP plan §4.7
 * "Batches"): such a change set lists its batch's change sets — reverting here reverts this
 * record's; a client's AI asks for the whole batch's, which waits in Pending MCP changes.
 *
 * A8 extends this page through `AI_CHANGE_DETAIL_EXTENSIONS` (header actions and sections),
 * never by editing it. */
import UndoIcon from '@mui/icons-material/Undo';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import * as aiChangesApi from '@/api/aiChanges';
import {
  ACTOR_KIND_LABELS,
  aiChangeKeys,
  channelLabel,
  CREDENTIAL_KIND_LABELS,
  needsKindPermissions,
  REVERT_KIND_PERMISSION_TEXT,
  shortId,
  statusColor,
  statusLabel,
  type AIChangeDetail,
  type AIChangeProposal,
  type AIChangeRef,
} from '@/api/aiChanges';
import { formatUsd } from '@/api/aiSpend';
import { ConsoleApiError } from '@/api/client';
import { PageHeader } from '@/components/PageHeader';
import { Field, FieldGrid, LinkField, SectionCard } from '@/components/ReadView';
import { UntrustedInline } from '@/components/UntrustedText';
import { operatorActions } from '@/components/ValueActions/identity';
import {
  act,
  CopyableId,
  DetailSubtitle,
  utcTitle,
  ValueActions,
} from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate, formatRelativeTime } from '@/utils/format';
import { aiChangesHref } from '@/utils/gridLinks';

import { AIChangeEntries } from './AIChangeEntries';
import {
  actorActions,
  aiChangeHref,
  aiChangeSpendHref,
  batchActions,
  batchHref,
  changeSetActions,
  channelActions,
  clientActions,
  ipActions,
  momentActions,
  tenantGrid,
} from './aiChangeLinks';
import { AIChangeLinkedHistory } from './AIChangeLinkedHistory';
import { AI_CHANGE_DETAIL_EXTENSIONS } from './extensions';
import { ITEM_ACTION } from './itemActions';
import { RevertChangeSetDialog } from './RevertChangeSetDialog';
import { CHANGE_REASON_LABEL, ItemNamesLegend, ReasonValue } from './theirWords';

export const CHANGE_SETS_OFF_TEXT =
  'Change sets are not enabled yet (CHANGE_SETS): the revert plan can be read, but a revert cannot run.';

export function AIChangeDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: aiChangeKeys.detail(id),
    queryFn: () => aiChangesApi.retrieve(id),
    enabled: id !== '',
  });

  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.isError) {
    const status = query.error instanceof ConsoleApiError ? query.error.status : 0;
    return (
      <Box>
        <PageHeader title="AI change" helpStory="ai-changes" />
        <Alert severity={status === 404 ? 'info' : 'error'}>
          {status === 404
            ? 'There is no AI change set with this id.'
            : status === 403
              ? 'You can’t see AI changes: it needs the console_ai_changes_view permission.'
              : `Couldn’t load the change set: ${query.error.message}`}
        </Alert>
      </Box>
    );
  }
  return <ChangeSetView change={query.data} />;
}

function ChangeSetView({ change }: { change: AIChangeDetail }) {
  const { hasPerm } = usePermissions();
  const [revertOpen, setRevertOpen] = useState(false);
  const canRevert = change.permissions.revert && change.revertable;
  // P3-16: the operator may revert in general, but not this change set's kinds of record.
  const kindBlocked =
    !change.permissions.revert &&
    change.revertable &&
    needsKindPermissions(change.domain) &&
    (hasPerm('console_ai_changes_revert') || hasPerm('console_notification_mcp_write'));

  const tenantPart = change.tenant ? (
    <ValueActions
      key="tenant"
      actions={operatorActions(change.tenant, tenantGrid(change.tenant.id))}
    >
      {change.tenant.name || change.tenant.code}
    </ValueActions>
  ) : (
    change.tenant_code || 'No tenant'
  );

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        helpStory="ai-changes"
        title={`AI change ${shortId(change.id)}`}
        titleAdornment={
          <Chip
            size="small"
            color={statusColor(change.status)}
            label={statusLabel(change.status)}
            data-ai-change-status
          />
        }
        subtitle={
          <DetailSubtitle
            nowrap
            parts={[
              tenantPart,
              <ValueActions key="channel" actions={channelActions(change.channel)}>
                {channelLabel(change.channel)}
              </ValueActions>,
              change.domain,
              <ValueActions
                key="when"
                title={utcTitle(change.created_ts)}
                actions={momentActions(change.created_ts)}
              >
                {formatDate(change.created_ts)}
              </ValueActions>,
              <ValueActions key="id" actions={[act.copy(change.id, 'Copy change-set id')]}>
                ID {change.id}
              </ValueActions>,
            ]}
          />
        }
        actions={
          <>
            {AI_CHANGE_DETAIL_EXTENSIONS.map(({ key, Action }) =>
              Action ? <Action key={key} change={change} /> : null,
            )}
            {canRevert &&
              (change.enabled ? (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<UndoIcon />}
                  onClick={() => setRevertOpen(true)}
                >
                  Revert change set…
                </Button>
              ) : (
                <Tooltip title={CHANGE_SETS_OFF_TEXT}>
                  <span>
                    <Button variant="outlined" color="warning" startIcon={<UndoIcon />} disabled>
                      Revert change set…
                    </Button>
                  </span>
                </Tooltip>
              ))}
            {kindBlocked && (
              <Tooltip title={REVERT_KIND_PERMISSION_TEXT}>
                <span>
                  <Button variant="outlined" color="warning" startIcon={<UndoIcon />} disabled>
                    Revert change set…
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        }
      />

      {change.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This change set failed and rolled back — nothing it planned was applied.
          {change.error ? ` ${change.error}` : ''}
        </Alert>
      )}
      {!change.revertable && change.revert_refusal && change.status !== 'failed' && (
        <Alert severity="info" sx={{ mb: 2 }} data-revert-refusal>
          {change.revert_refusal}
        </Alert>
      )}

      <SummarySection change={change} canSeeSpend={hasPerm('console_ai_spend_view')} />

      {AI_CHANGE_DETAIL_EXTENSIONS.map(({ key, Section }) =>
        Section ? <Section key={key} change={change} /> : null,
      )}

      {change.proposal && (
        <ProposalSection proposal={change.proposal} canOpen={hasPerm('console_composition_view')} />
      )}

      {change.batch_id && <BatchSection change={change} batchId={change.batch_id} />}

      {(change.original || change.reverts.length > 0) && <RevertsSection change={change} />}

      <SectionCard title={`Records (${change.entry_count})`}>
        <AIChangeEntries changeId={change.id} />
      </SectionCard>

      <SectionCard title="Linked history">
        <AIChangeLinkedHistory changeId={change.id} />
      </SectionCard>

      {revertOpen && <RevertChangeSetDialog change={change} onClose={() => setRevertOpen(false)} />}
    </Box>
  );
}

function Moment({ iso }: { iso: string | null }) {
  if (!iso) return null;
  return (
    <ValueActions title={utcTitle(iso)} actions={momentActions(iso)}>
      {formatDate(iso)}
    </ValueActions>
  );
}

function SummarySection({ change, canSeeSpend }: { change: AIChangeDetail; canSeeSpend: boolean }) {
  const client = change.oauth_client_id
    ? { id: change.oauth_client_id, name: change.oauth_client_name || change.oauth_client_id }
    : change.client;
  const records = `${change.entry_count} — ${change.ops.create} created · ${change.ops.update} updated · ${change.ops.delete} deleted`;

  return (
    <SectionCard title="Who, how and when">
      <FieldGrid>
        <Field
          label="Applied by"
          value={
            change.actor ? (
              <>
                <ValueActions actions={actorActions(change.actor)}>
                  {change.actor.email}
                </ValueActions>
                {` · ${ACTOR_KIND_LABELS[change.actor_kind]}`}
              </>
            ) : (
              ACTOR_KIND_LABELS[change.actor_kind]
            )
          }
        />
        <Field
          label="How"
          value={`${channelLabel(change.channel)} · ${CREDENTIAL_KIND_LABELS[change.credential_kind]}`}
        />
        {client && (
          <Field
            label="MCP client"
            value={client.name === client.id ? client.id : `${client.name} (${client.id})`}
            actions={clientActions(client)}
          />
        )}
        {change.oauth_grant_id && (
          <Field
            label="MCP grant"
            value={<CopyableId value={change.oauth_grant_id} label="Copy grant id" />}
          />
        )}
        {change.console_token_fingerprint && (
          <Field
            label="Console token"
            value={<CopyableId value={change.console_token_fingerprint} label="Copy fingerprint" />}
          />
        )}
        {change.mcp_session_id && (
          <Field
            label="MCP session (legacy)"
            value={<CopyableId value={change.mcp_session_id} label="Copy session id" />}
          />
        )}
        <Field
          label="Request IP"
          value={change.request_ip}
          {...(change.request_ip ? { actions: ipActions(change.request_ip) } : {})}
        />
        <Field
          label="Login IP"
          value={change.login_ip}
          {...(change.login_ip ? { actions: ipActions(change.login_ip) } : {})}
        />
        <Field label="Created" value={<Moment iso={change.created_ts} />} />
        <Field
          label="Applied"
          value={change.applied_ts ? <Moment iso={change.applied_ts} /> : null}
        />
        <Field
          label="Finished"
          value={change.finished_ts ? <Moment iso={change.finished_ts} /> : null}
        />
        <Field label="Records" value={records} />
        {canSeeSpend ? (
          <LinkField
            label="AI cost"
            to={aiChangeSpendHref(change.id)}
            text={formatUsd(change.cost_usd)}
            actions={[act.filter(aiChangeSpendHref(change.id), 'The AI calls of this change set')]}
          />
        ) : (
          <Field label="AI cost" value={formatUsd(change.cost_usd)} />
        )}
      </FieldGrid>
      <Field label={CHANGE_REASON_LABEL} value={<ReasonValue text={change.reason} />} />
      <Field label="Summary" value={change.summary} />
      <Field label="User agent" value={change.request_user_agent} />
      <FieldGrid>
        <Field
          label="Receipt"
          value={
            change.receipt_sha256 ? (
              <CopyableId value={change.receipt_sha256} label="Copy receipt hash" />
            ) : null
          }
        />
        <Field
          label="Manifest"
          value={
            change.manifest_sha256 ? (
              <CopyableId value={change.manifest_sha256} label="Copy manifest hash" />
            ) : null
          }
        />
        {/* A key in the private change-set store, never a link: the files are read through the
            API, record by record, below. */}
        <Field
          label="Snapshot store key"
          value={
            change.s3_prefix ? <CopyableId value={change.s3_prefix} label="Copy store key" /> : null
          }
        />
      </FieldGrid>
    </SectionCard>
  );
}

/** A recorded answer: the option's label (and free text) when it has one, else the JSON. */
function answerText(answer: unknown): string {
  if (answer === null || answer === undefined) return '—';
  if (typeof answer === 'string') return answer;
  if (typeof answer === 'object') {
    const a = answer as { label?: unknown; free_text?: unknown };
    const label = typeof a.label === 'string' ? a.label : '';
    const free = typeof a.free_text === 'string' && a.free_text ? `“${a.free_text}”` : '';
    if (label || free) return [label, free].filter(Boolean).join(' — ');
  }
  return JSON.stringify(answer);
}

function ProposalSection({ proposal, canOpen }: { proposal: AIChangeProposal; canOpen: boolean }) {
  const href = `/notifications/design-imports/proposals/${proposal.id}`;
  return (
    <SectionCard
      title={`Proposal #${proposal.id}`}
      action={
        canOpen ? (
          <Link component={RouterLink} to={href} underline="hover" variant="body2">
            Open the proposal
          </Link>
        ) : undefined
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {proposal.origin} proposal · {proposal.status} · created{' '}
        {formatRelativeTime(proposal.created_ts)}
        {proposal.created_by && (
          <>
            {' by '}
            <ValueActions actions={actorActions(proposal.created_by)}>
              {proposal.created_by.email}
            </ValueActions>
          </>
        )}
      </Typography>

      {proposal.items.length > 0 && <ItemNamesLegend />}
      {proposal.items.length > 0 && (
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small" aria-label="Proposal items">
            <TableHead>
              <TableRow>
                <TableCell>Kind</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proposal.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.kind}</TableCell>
                  <TableCell>{ITEM_ACTION[item.action]}</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    {item.label ? <UntrustedInline text={item.label} /> : `Item #${item.id}`}
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {item.target_id !== null ? `#${item.target_id}` : '—'}
                  </TableCell>
                  <TableCell>{item.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {proposal.questions.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Answered questions
          </Typography>
          {proposal.questions.map((q) => (
            <Box key={q.id} sx={{ mb: 1.5 }} data-proposal-question={q.id}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {q.prompt}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {answerText(q.answer)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {q.answered_by ? (
                  <ValueActions actions={actorActions(q.answered_by)}>
                    {q.answered_by.email}
                  </ValueActions>
                ) : (
                  'unknown'
                )}
                {q.answered_ts ? ` · ${formatDate(q.answered_ts)}` : ''}
                {q.blocking ? ' · blocking' : ''}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </SectionCard>
  );
}

function RefLine({
  refItem,
  lead,
  showChannel = true,
}: {
  refItem: AIChangeRef;
  lead: ReactNode;
  showChannel?: boolean;
}) {
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}
      data-change-set-ref={refItem.id}
    >
      <Typography variant="body2" component="span">
        {lead}{' '}
        <ValueActions actions={changeSetActions(refItem.id)}>
          <Link component={RouterLink} to={aiChangeHref(refItem.id)} underline="hover">
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {shortId(refItem.id)}
            </Box>
          </Link>
        </ValueActions>
      </Typography>
      {showChannel && (
        <Chip size="small" variant="outlined" label={channelLabel(refItem.channel)} />
      )}
      <Chip size="small" color={statusColor(refItem.status)} label={statusLabel(refItem.status)} />
      <Tooltip title={formatDate(refItem.created_ts)} describeChild>
        <Typography variant="caption" color="text.secondary">
          {formatRelativeTime(refItem.created_ts)}
        </Typography>
      </Tooltip>
    </Box>
  );
}

function RevertsSection({ change }: { change: AIChangeDetail }) {
  return (
    <SectionCard
      title="Reverts"
      action={
        change.reverts.length > 0 ? (
          <Link
            component={RouterLink}
            to={aiChangesHref({ revert_of: change.id })}
            underline="hover"
            variant="body2"
          >
            In the grid
          </Link>
        ) : undefined
      }
    >
      {change.original && <RefLine refItem={change.original} lead="This change set reverts" />}
      {change.reverts.map((r) => (
        // Any status, newest first: a failed revert is listed too, and says so.
        <RefLine key={r.id} refItem={r} lead="Revert" showChannel={false} />
      ))}
      {change.original && (
        <Typography variant="caption" color="text.secondary" component="div">
          To redo what a revert undid, revert the revert.
        </Typography>
      )}
    </SectionCard>
  );
}

/** The most change sets of one batch listed here (the grid has the rest). */
const BATCH_SHOWN = 50;

/** The batch this change set belongs to: one content apply, one change set per record. */
function BatchSection({ change, batchId }: { change: AIChangeDetail; batchId: string }) {
  const query = useQuery({
    queryKey: [...aiChangeKeys.batch(batchId), BATCH_SHOWN],
    queryFn: () =>
      aiChangesApi.list({ batch_id: batchId, ordering: 'created_ts', limit: BATCH_SHOWN }),
  });
  const members = query.data?.results ?? [];
  const more = (query.data?.count ?? 0) - members.length;
  return (
    <Box data-batch-section>
      <SectionCard
        title="Batch"
        action={
          <Link component={RouterLink} to={batchHref(batchId)} underline="hover" variant="body2">
            All its change sets
          </Link>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          One content apply records one change set per record, all in batch{' '}
          <ValueActions actions={batchActions(batchId)}>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {shortId(batchId)}
            </Box>
          </ValueActions>
          . Reverting here reverts this record’s change set only; a client’s AI may ask to revert the
          whole batch, which then waits in Pending MCP changes.
        </Typography>
        {query.isPending && <CircularProgress size={20} />}
        {query.isError && (
          <Alert severity="error">Couldn’t list the batch: {query.error.message}</Alert>
        )}
        {members.length > 0 && (
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {members.map((m) => (
              <Box
                component="li"
                key={m.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}
              >
                {m.id === change.id ? (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    This change set
                  </Typography>
                ) : (
                  <ValueActions actions={changeSetActions(m.id)}>
                    <Link component={RouterLink} to={aiChangeHref(m.id)} underline="hover">
                      <Box component="span" sx={{ fontFamily: 'monospace' }}>
                        {shortId(m.id)}
                      </Box>
                    </Link>
                  </ValueActions>
                )}
                <Typography variant="body2" component="span">
                  {m.summary || `${m.entry_count} records`}
                </Typography>
                <Chip size="small" color={statusColor(m.status)} label={statusLabel(m.status)} />
              </Box>
            ))}
          </Box>
        )}
        {more > 0 && (
          <Typography variant="caption" color="text.secondary">
            {`${more} more in the grid.`}
          </Typography>
        )}
      </SectionCard>
    </Box>
  );
}
